import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'
import { useCheckout } from 'public/components/online_ordering/CheckoutContext'
import {
  PaymentOption,
  usePayment
} from 'public/components/online_ordering/PaymentContext'
import { useForm } from 'react-hook-form'
import { getCustomerInfo } from 'public/components/default_template/online_ordering/checkout/checkoutUtils'
import { useCallback } from 'react'

export const useCanSubmitOrder = () => {
  const { customer } = useCustomer()
  const isIntlRx = useIsIntlRestaurant()
  const { giftCardAppliedAmount, orderTotal, setShowTipWarning } = useCheckout()
  const { tipAmount, paymentOption, isPaymentValid } = usePayment()
  const formMethods = useForm({
    mode: 'onTouched',
    defaultValues: getCustomerInfo(customer)
  })

  const { formState } = formMethods

  const canSubmitOrder = useCallback(() => {
    let canSubmit
    const paymentValid =
      paymentOption !== null &&
      (paymentOption === PaymentOption.UponReceipt ||
        orderTotal === 0 ||
        (paymentOption === PaymentOption.PayNow && isPaymentValid))

    const spiContainer = document.getElementById('creditCardFormSpiContainer')
    spiContainer?.classList.remove('invalid')

    // Order is important here:
    // 1. INTL Rxs use this hook on the 'continue to payment' button, so they don't need to check if payment info is valid, just form state
    // 2. For non-INTL Rxs, form fields get validated first, and trigger React form validation
    // 3. If all React form fields are valid, we manually imitate React form validation for SPI form if not valid
    // 4. If form fields and payment info are both valid, we check if we can apply the gift card safely
    // 5. If everything's good, we can submit
    if (isIntlRx) {
      canSubmit = formState.isValid && !formState.isSubmitting
    } else if (!formState.isValid || formState.isSubmitting) {
      canSubmit = false
    } else if (!paymentValid) {
      spiContainer?.scrollIntoView({
        block: 'center',
        behavior: 'smooth'
      })
      spiContainer?.classList.add('invalid')
      canSubmit = false
    } else if (giftCardAppliedAmount > 0 && tipAmount > 0 && !isPaymentValid) {
      // OO does not support allowing GC's to cover tip amounts, we need to make sure a proper payment has been
      // provided in this case
      canSubmit = false
      setShowTipWarning(true)
    } else {
      canSubmit = true
    }

    return canSubmit
  }, [
    formState.isSubmitting,
    formState.isValid,
    giftCardAppliedAmount,
    isIntlRx,
    isPaymentValid,
    orderTotal,
    paymentOption,
    setShowTipWarning,
    tipAmount
  ])

  return canSubmitOrder
}
